import type {
  EstimatorReportComparisonDto,
  EstimatorReportDto,
  EstimatorReportGroupDto,
  EstimatorReportSearchResultDto,
  PresentationDto,
  PresentationItemDto,
  ProjectDto,
  TenantDto,
  UserDto,
} from "@/dto";
// here we need to import the router instance instead of using the composable
// because inject s not available
import { RouteNames, router } from "@/router";
import { getErrorMessage } from "@/services/utils";
import { useToastsStore } from "@/stores/toasts";

const BASE_URL = import.meta.env.VITE_API_BASE_URL;
const IS_AUTHENTICATED_LOCAL_STORAGE_KEY = "probabl-is-authenticated";
const EXPIRES_AT_LOCAL_STORAGE_KEY = "probabl-expires-at";

export function useApi() {
  function _reportError(message: string, showToast: boolean = true) {
    console.error(message);
    if (showToast) {
      try {
        const toastsStore = useToastsStore();
        toastsStore.addToast(message, "error", { duration: 10 });
      } catch {}
    }
  }

  function _checkResponseStatus(r: Response, attendedStatusCode: number) {
    if (r.status !== attendedStatusCode) {
      throw new Error(`Server responded with unexpected status code: ${r.status}`);
    }
  }

  async function _ensureAuthentication() {
    const isAuthenticated = await isUserAuthenticated();
    if (!isAuthenticated) {
      router.push({ name: RouteNames.Logout });
    }
  }

  function isMarkedAuthenticated() {
    return localStorage.getItem(IS_AUTHENTICATED_LOCAL_STORAGE_KEY) === "true";
  }

  function getExpiresAt() {
    return new Date(localStorage.getItem(EXPIRES_AT_LOCAL_STORAGE_KEY) ?? new Date().toISOString());
  }

  async function isUserAuthenticated() {
    const now = new Date();
    const expiresAt = getExpiresAt();
    if (now >= expiresAt && isMarkedAuthenticated()) {
      const { access_token, refresh_token, expires_at } = await refreshToken();
      if (access_token && refresh_token && expires_at) {
        markAuthenticated(expires_at);
      }
    }
    return localStorage.getItem(IS_AUTHENTICATED_LOCAL_STORAGE_KEY) === "true";
  }

  function markAuthenticated(expires_at: string) {
    // persist
    localStorage.setItem(IS_AUTHENTICATED_LOCAL_STORAGE_KEY, "true");
    localStorage.setItem(EXPIRES_AT_LOCAL_STORAGE_KEY, expires_at);
  }

  function markUnauthenticated() {
    localStorage.removeItem(IS_AUTHENTICATED_LOCAL_STORAGE_KEY);
    localStorage.removeItem(EXPIRES_AT_LOCAL_STORAGE_KEY);
  }

  const _FETCH_DEFAULT_OPTIONS: RequestInit = {
    headers: {
      "Content-Type": "application/json",
      "X-SKH-Content-Type": "front",
    },
    credentials: "include",
  };

  /**
   * Start a login process.
   */
  async function login(successUri: string) {
    try {
      const uri = encodeURIComponent(successUri);
      const request = await fetch(`${BASE_URL}/identity/oauth/login?success_uri=${uri}`, {
        method: "GET",
        headers: _FETCH_DEFAULT_OPTIONS.headers,
      });
      _checkResponseStatus(request, 200);
      const { authorization_url } = await request.json();
      if (authorization_url) {
        window.location.assign(authorization_url);
      }
    } catch (error) {
      _reportError(getErrorMessage(error));
    }
  }

  /**
   * Logout the user by invaldiating there token backend side
   */
  async function logout() {
    try {
      await _ensureAuthentication();
      const request = await fetch(`${BASE_URL}/identity/oauth/logout`, {
        method: "POST",
        ..._FETCH_DEFAULT_OPTIONS,
      });
      _checkResponseStatus(request, 204);
    } catch (error) {
      _reportError(getErrorMessage(error));
    } finally {
      markUnauthenticated();
    }
  }

  /**
   * Check a device token against the IdP
   * @param state the state given by the IdP
   * @param user_code the otp
   * @returns true if success false otherwise
   */
  async function deviceCallback(state: string, user_code: string) {
    try {
      const request = await fetch(`${BASE_URL}/identity/oauth/device/callback`, {
        method: "POST",
        headers: _FETCH_DEFAULT_OPTIONS.headers,
        body: JSON.stringify({ state, user_code }),
      });
      _checkResponseStatus(request, 201);
      return true;
    } catch (error) {
      _reportError(getErrorMessage(error));
    }
    return false;
  }

  /**
   * Refresh token
   */
  async function refreshToken() {
    type R = { access_token?: string; refresh_token?: string; expires_at?: string };
    try {
      const request = await fetch(`${BASE_URL}/identity/oauth/token/refresh`, {
        method: "POST",
        ..._FETCH_DEFAULT_OPTIONS,
        body: "{}",
      });
      _checkResponseStatus(request, 200);
      return (await request.json()) as R;
    } catch (error) {
      _reportError(getErrorMessage(error), false);
      markUnauthenticated();
      router.replace({ name: RouteNames.Logout });
    }
    return {} as R;
  }

  /**
   * Fetch a tenant the user has access to.
   * @param tenantId
   * @returns a tenant dto
   */
  async function fetchTenant(tenantId: number) {
    try {
      await _ensureAuthentication();
      const request = await fetch(`${BASE_URL}/identity/tenants/${tenantId}`, {
        method: "GET",
        ..._FETCH_DEFAULT_OPTIONS,
      });
      _checkResponseStatus(request, 200);
      const data = await request.json();
      return data as TenantDto;
    } catch (error) {
      _reportError(getErrorMessage(error));
    }
    return null;
  }

  /**
   * Get the list of tenants the user has access to.
   * @returns a list of tenants
   */
  async function listTenants() {
    try {
      await _ensureAuthentication();
      const request = await fetch(`${BASE_URL}/identity/tenants/`, {
        method: "GET",
        ..._FETCH_DEFAULT_OPTIONS,
      });
      _checkResponseStatus(request, 200);
      const data = await request.json();
      const tenants: TenantDto[] = data["items"];
      return tenants;
    } catch (error) {
      _reportError(getErrorMessage(error));
    }
    return [] as TenantDto[];
  }

  /**
   * Get the list of project the user has access to.
   * @param tenantName tenant name in which we want to list project
   * @returns a list of project or null if not found
   */
  async function listProjects(tenantName: string) {
    try {
      await _ensureAuthentication();
      const request = await fetch(`${BASE_URL}/projects/${tenantName}`, {
        method: "GET",
        ..._FETCH_DEFAULT_OPTIONS,
      });
      _checkResponseStatus(request, 200);
      const projects: ProjectDto[] = await request.json();
      return projects;
    } catch (error) {
      _reportError(getErrorMessage(error));
    }
    return null;
  }

  /**
   * Delete project the user has access to.
   * @param tenantName tenant name to which the project belongs
   * @param projectName the project's name
   * @returns a list of project
   * @returns true if delete has been done false otherwise.
   */
  async function deleteProject(tenantName: string, projectName: string) {
    try {
      await _ensureAuthentication();
      const request = await fetch(`${BASE_URL}/projects/${tenantName}/${projectName}`, {
        method: "DELETE",
        ..._FETCH_DEFAULT_OPTIONS,
      });
      _checkResponseStatus(request, 204);
      return true;
    } catch (error) {
      _reportError(getErrorMessage(error));
    }
    return false;
  }

  /**
   * Get the estimator report groups for a given project
   * @param tenantName tenant name to which the project belongs
   * @param projectName the project's name
   * @returns a list of experiments groups or null if not found
   */
  async function listEstimatorReportGroups(tenantName: string, projectName: string) {
    try {
      await _ensureAuthentication();
      const request = await fetch(
        `${BASE_URL}/projects/${tenantName}/${projectName}/estimator-reports/groups`,
        {
          method: "GET",
          ..._FETCH_DEFAULT_OPTIONS,
        }
      );
      _checkResponseStatus(request, 200);
      const data = await request.json();
      const groups: EstimatorReportGroupDto[] = data["groups"];
      return groups;
    } catch (error) {
      _reportError(getErrorMessage(error));
    }
    return null;
  }

  /**
   * Filter estimator reports
   * @param tenantName tenant name to which the project belongs
   * @param projectName the project's name
   * @param datasetFingerprint the dataset  to filter from
   * @param searchParams the search params to send
   * @returns a list of experiments groups
   */
  async function filterEstimatorReports(
    tenantName: string,
    projectName: string,
    datasetFingerprint: string,
    searchParams: URLSearchParams
  ) {
    try {
      await _ensureAuthentication();
      searchParams.append("dataset_fingerprint", datasetFingerprint);
      const request = await fetch(
        `${BASE_URL}/projects/${tenantName}/${projectName}/estimator-reports/filter?${searchParams}`,
        {
          method: "GET",
          ..._FETCH_DEFAULT_OPTIONS,
        }
      );
      _checkResponseStatus(request, 200);
      const data = await request.json();
      return data;
    } catch (error) {
      _reportError(getErrorMessage(error));
    }
    return [] as EstimatorReportSearchResultDto[];
  }

  /**
   * Fetch estimator report
   * @param tenantName tenant name to which the project belongs
   * @param projectName the project's name
   * @param reportId the dataset  to filter from
   */
  async function fetchEstimatorReport(tenantName: string, projectName: string, reportId: number) {
    try {
      await _ensureAuthentication();
      const request = await fetch(
        `${BASE_URL}/projects/${tenantName}/${projectName}/estimator-reports/${reportId}`,
        {
          method: "GET",
          ..._FETCH_DEFAULT_OPTIONS,
        }
      );
      _checkResponseStatus(request, 200);
      const data = await request.json();
      return data as EstimatorReportDto;
    } catch (error) {
      _reportError(getErrorMessage(error));
    }
    return null;
  }

  /**
   * Fetch a project presentation list
   * @param tenantName tenant name to which the project belongs
   * @param projectName the project's name
   */
  async function listProjectPresentations(tenantName: string, projectName: string) {
    try {
      await _ensureAuthentication();
      const request = await fetch(
        `${BASE_URL}/projects/${tenantName}/${projectName}/presentations/`,
        {
          method: "GET",
          ..._FETCH_DEFAULT_OPTIONS,
        }
      );
      _checkResponseStatus(request, 200);
      const data = await request.json();
      return data as PresentationDto[];
    } catch (error) {
      _reportError(getErrorMessage(error));
    }
    return [];
  }

  /**
   * Fetch a project presentation
   * @param tenantName tenant name to which the project belongs
   * @param projectName the project's name
   * @param id the presentation id
   */
  async function fecthProjectPresentation(tenantName: string, projectName: string, id: number) {
    try {
      await _ensureAuthentication();
      const request = await fetch(
        `${BASE_URL}/projects/${tenantName}/${projectName}/presentations/${id}`,
        {
          method: "GET",
          ..._FETCH_DEFAULT_OPTIONS,
        }
      );
      _checkResponseStatus(request, 200);
      const data = await request.json();
      return data as PresentationDto;
    } catch (error) {
      _reportError(getErrorMessage(error));
    }
    return null;
  }

  /**
   * Create a project presentation
   * @param tenantName tenant name to which the project belongs
   * @param projectName the project's name
   * @param name the presentation name
   */
  async function createProjectPresentation(tenantName: string, projectName: string, name: string) {
    try {
      await _ensureAuthentication();
      const request = await fetch(
        `${BASE_URL}/projects/${tenantName}/${projectName}/presentations/`,
        {
          method: "POST",
          ..._FETCH_DEFAULT_OPTIONS,
          body: JSON.stringify({ name }),
        }
      );
      _checkResponseStatus(request, 201);
      const data = await request.json();
      return data as PresentationDto;
    } catch (error) {
      _reportError(getErrorMessage(error));
    }
    return null;
  }

  /**
   * Set the content of a project presentation
   * @param tenantName tenant name to which the project belongs
   * @param projectName the project's name
   * @param id the presentation id
   * @param content the content ids to put in the presentation
   * @returns true if update has been done false otherwise.
   */
  async function setProjectPresentationContent(
    tenantName: string,
    projectName: string,
    id: number,
    content: number[]
  ) {
    try {
      await _ensureAuthentication();
      const request = await fetch(
        `${BASE_URL}/projects/${tenantName}/${projectName}/presentations/${id}/items`,
        {
          method: "PUT",
          ..._FETCH_DEFAULT_OPTIONS,
          body: JSON.stringify(content),
        }
      );
      _checkResponseStatus(request, 201);
      return true;
    } catch (error) {
      _reportError(getErrorMessage(error));
    }
    return false;
  }

  /**
   * Create a presentation item
   * @param tenantName tenant name to which the project belongs
   * @param projectName the project's name
   * @param id the presentation id
   * @param note an note on the item
   * @param media_type the content media_type
   * @param value the content value
   * @param report_id id of the relative report
   * @param comparison_id id of the relative comparison
   * @returns the id of the newly create item -1 if something went wrong
   */
  async function createPresentationItem(
    tenantName: string,
    projectName: string,
    id: number,
    name: string,
    note: string,
    media_type: string,
    value: any,
    {
      report_id = null,
      comparison_id = null,
    }: { report_id: number | null; comparison_id: number | null }
  ) {
    try {
      await _ensureAuthentication();
      const request = await fetch(
        `${BASE_URL}/projects/${tenantName}/${projectName}/presentations/${id}/items`,
        {
          method: "POST",
          ..._FETCH_DEFAULT_OPTIONS,
          body: JSON.stringify({
            report_id,
            comparison_id,
            name,
            note,
            media_type,
            value,
          }),
        }
      );
      _checkResponseStatus(request, 201);
      const data = await request.json();
      return data.item_id as number;
    } catch (error) {
      _reportError(getErrorMessage(error));
    }
    return -1;
  }

  /**
   * Create a presentation item
   * @param tenantName tenant name to which the project belongs
   * @param projectName the project's name
   * @param presentationId the presentation id
   * @param itemId the presentation item id
   * @param note an note on the item
   * @returns true if deletion has been done false otherwise.
   */
  async function updatePresentationItemNote(
    tenantName: string,
    projectName: string,
    presentationId: number,
    itemId: number,
    note: string
  ) {
    try {
      _ensureAuthentication();
      const request = await fetch(
        `${BASE_URL}/projects/${tenantName}/${projectName}/presentations/${presentationId}/items/${itemId}`,
        {
          method: "PUT",
          ..._FETCH_DEFAULT_OPTIONS,
          body: JSON.stringify({
            note,
          }),
        }
      );
      _checkResponseStatus(request, 200);
      return true;
    } catch (error) {
      _reportError(getErrorMessage(error));
    }
    return false;
  }

  /**
   * List all presentation items
   * @param tenantName tenant name to which the project belongs
   * @param projectName the project's name
   * @param id the presentation id
   */
  async function listPresentationItems(tenantName: string, projectName: string, id: number) {
    try {
      await _ensureAuthentication();
      const request = await fetch(
        `${BASE_URL}/projects/${tenantName}/${projectName}/presentations/${id}/items`,
        {
          method: "GET",
          ..._FETCH_DEFAULT_OPTIONS,
        }
      );
      _checkResponseStatus(request, 200);
      const data = await request.json();
      return data as PresentationItemDto[];
    } catch (error) {
      _reportError(getErrorMessage(error));
    }
    return [];
  }

  /**
   * Delete a presentation
   * @param tenantName tenant name to which the project belongs
   * @param projectName the project's name
   * @param id the presentation id
   * @returns true if deletion has been done false otherwise.
   */
  async function deletePresentation(tenantName: string, projectName: string, id: number) {
    try {
      await _ensureAuthentication();
      const request = await fetch(
        `${BASE_URL}/projects/${tenantName}/${projectName}/presentations/${id}`,
        {
          method: "DELETE",
          ..._FETCH_DEFAULT_OPTIONS,
        }
      );
      _checkResponseStatus(request, 204);
      return true;
    } catch (error) {
      _reportError(getErrorMessage(error));
    }
    return false;
  }

  /**
   * Delete a presentation item
   * @param tenantName tenant name to which the project belongs
   * @param projectName the project's name
   * @param presentationId the presentation id
   * @param itemId the presentation item id
   * @returns true if deletion has been done false otherwise.
   */
  async function deletePresentationItem(
    tenantName: string,
    projectName: string,
    presentationId: number,
    itemId: number
  ) {
    try {
      await _ensureAuthentication();
      const request = await fetch(
        `${BASE_URL}/projects/${tenantName}/${projectName}/presentations/${presentationId}/items/${itemId}`,
        {
          method: "DELETE",
          ..._FETCH_DEFAULT_OPTIONS,
        }
      );
      _checkResponseStatus(request, 204);
      return true;
    } catch (error) {
      _reportError(getErrorMessage(error));
    }
    return false;
  }

  /**
   * Fetch a user by it's id
   * @param userId the if of the user to get
   */
  async function fetchUser(userId: string) {
    try {
      await _ensureAuthentication();
      const request = await fetch(`${BASE_URL}/identity/users/${userId}`, {
        method: "GET",
        ..._FETCH_DEFAULT_OPTIONS,
      });
      _checkResponseStatus(request, 200);
      const data = await request.json();
      return data as UserDto;
    } catch (error) {
      _reportError(getErrorMessage(error));
    }
    return null;
  }

  /**
   * Fetch current user's profile.
   */
  async function fetchMe() {
    try {
      await _ensureAuthentication();
      const request = await fetch(`${BASE_URL}/identity/users/me`, {
        method: "GET",
        ..._FETCH_DEFAULT_OPTIONS,
      });
      _checkResponseStatus(request, 200);
      const data = await request.json();
      return data as UserDto;
    } catch (error) {
      _reportError(getErrorMessage(error));
    }
    return null;
  }

  /**
   * Fetch the list of comparisons of a given project.
   * @param tenantName tenant name to which the project belongs
   * @param projectName the project's name
   * @returns a list of comparisons.
   */
  async function listComparisons(tenantName: string, projectName: string) {
    try {
      await _ensureAuthentication();
      const request = await fetch(`${BASE_URL}/projects/${tenantName}/${projectName}/comparisons`, {
        method: "GET",
        ..._FETCH_DEFAULT_OPTIONS,
      });
      _checkResponseStatus(request, 200);
      const data = await request.json();
      return data as EstimatorReportComparisonDto[];
    } catch (error) {
      _reportError(getErrorMessage(error));
    }
    return [];
  }

  /**
   * Fetch a comparison from a given project.
   * @param tenantName tenant name to which the project belongs
   * @param projectName the project's name
   * @param comparisonId the comparison id
   * @returns a comparison or null if something went wrong.
   */
  async function fetchComparison(tenantName: string, projectName: string, comparisonId: number) {
    try {
      await _ensureAuthentication();
      const request = await fetch(
        `${BASE_URL}/projects/${tenantName}/${projectName}/comparisons/${comparisonId}`,
        {
          method: "GET",
          ..._FETCH_DEFAULT_OPTIONS,
        }
      );
      _checkResponseStatus(request, 200);
      const data = await request.json();
      return data as EstimatorReportComparisonDto;
    } catch (error) {
      _reportError(getErrorMessage(error));
    }
    return null;
  }

  /**
   * Create a comparison in a given project.
   * @param tenantName tenant name to which the project belongs
   * @param projectName the project's name
   * @param name the comparison's name
   * @param comparedReportsIds the complete list of compared reports ids
   * @param bestTradeOffsIds the list of chosen reports ids
   * @param bestTradeOffsExplanations a dict containing explanation for each choice
   * @returns the newly created comparison or null if something went wrong.
   */
  async function createComparisons(
    tenantName: string,
    projectName: string,
    name: string,
    comparedReportsIds: number[],
    bestTradeOffsIds: number[],
    bestTradeOffsExplanations: Record<number, string>
  ) {
    try {
      await _ensureAuthentication();
      const request = await fetch(`${BASE_URL}/projects/${tenantName}/${projectName}/comparisons`, {
        method: "POST",
        ..._FETCH_DEFAULT_OPTIONS,
        body: JSON.stringify({
          name,
          compared_reports: comparedReportsIds,
          best_trade_offs: bestTradeOffsIds,
          explanations: bestTradeOffsExplanations,
        }),
      });
      _checkResponseStatus(request, 201);
      const data = await request.json();
      return data as EstimatorReportComparisonDto;
    } catch (error) {
      _reportError(getErrorMessage(error));
    }
    return null;
  }

  /**
   * Delete a comparison from a given project.
   * @param tenantName tenant name to which the project belongs
   * @param projectName the project's name
   * @param comparisonId the comparison id
   * @returns true if deletion has been done false otherwise.
   */
  async function deleteComparison(tenantName: string, projectName: string, comparisonId: number) {
    try {
      await _ensureAuthentication();
      const request = await fetch(
        `${BASE_URL}/projects/${tenantName}/${projectName}/comparisons/${comparisonId}`,
        {
          method: "DELETE",
          ..._FETCH_DEFAULT_OPTIONS,
        }
      );
      _checkResponseStatus(request, 204);
      return true;
    } catch (error) {
      _reportError(getErrorMessage(error));
    }
    return false;
  }

  return {
    markAuthenticated,
    isUserAuthenticated,
    login,
    logout,
    deviceCallback,
    refreshToken,
    fetchTenant,
    listTenants,
    listProjects,
    deleteProject,
    listEstimatorReportGroups,
    filterEstimatorReports,
    fetchEstimatorReport,
    listProjectPresentations,
    fecthProjectPresentation,
    createProjectPresentation,
    setProjectPresentationContent,
    createPresentationItem,
    updatePresentationItemNote,
    listPresentationItems,
    deletePresentation,
    deletePresentationItem,
    fetchUser,
    fetchMe,
    listComparisons,
    fetchComparison,
    createComparisons,
    deleteComparison,
  };
}
